import React, { useEffect, useRef, useState } from 'react';
import { Box, keyframes, usePrefersReducedMotion } from '@chakra-ui/react';

import { useBannerSize } from '../../../../common/hooks/use-banner-size';

// Every banner content renders at the same fixed height (see useBannerSize), so
// swapping the multipurpose banner's contents never changes the box size — we can
// dissolve the old content into the new one, stacked in the same constant-height,
// position:relative box. No layout shift, no cut.
//
// Implementation note: we deliberately do NOT use framer-motion's AnimatePresence
// here, nor a JS opacity toggle. Both rendered as an instant cut in the running app
// (a single rAF can flip opacity 0→1 before the 0 state ever paints). Instead the
// incoming layer is a freshly-keyed element with a CSS fade-in animation: a newly
// mounted element ALWAYS plays its animation from the start, so the dissolve is
// reliable. The outgoing layer stays fully painted underneath until the new one has
// faded in over it, then it's dropped.
const CROSSFADE_MS = 350;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

type Layer = { key: string; node: React.ReactNode };

type BannerContentTransitionProps = {
  // A stable identity for the current content. The crossfade only triggers when
  // this changes, so live data updates within the same mode don't re-animate.
  contentKey: string;
  children: React.ReactNode;
};

export const BannerContentTransition = ({ contentKey, children }: BannerContentTransitionProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { h } = useBannerSize();

  // The layer painted on top (the current content).
  const [current, setCurrent] = useState<Layer>({ key: contentKey, node: children });
  // The outgoing layer painted underneath while the new one fades in (null = settled).
  const [previous, setPrevious] = useState<Layer | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancel any in-flight fade on unmount.
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  useEffect(() => {
    if (prefersReducedMotion) {
      // Instant, stable swap for reduced-motion users. Cancel any in-flight crossfade
      // so a stale timeout can't drop `previous` on old timing after reduced-motion
      // has been enabled (e.g. OS setting flips while the app is open).
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setPrevious(null);
      if (current.key !== contentKey || current.node !== children) {
        setCurrent({ key: contentKey, node: children });
      }
      return;
    }

    if (contentKey === current.key) {
      // Same mode — refresh live data in place, no animation. Guard on identity so
      // our own state-driven re-renders don't loop.
      if (current.node !== children) {
        setCurrent({ key: contentKey, node: children });
      }
      return;
    }

    // Mode changed: keep the old content underneath, fade the new content in on top.
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setPrevious(current);
    setCurrent({ key: contentKey, node: children });
    timeoutRef.current = setTimeout(() => {
      setPrevious(null);
    }, CROSSFADE_MS);
  }, [contentKey, children, prefersReducedMotion, current]);

  if (prefersReducedMotion) {
    return <>{children}</>;
  }

  return (
    <Box position="relative" w="100%" h={h} data-testid="banner-content-transition">
      {previous && (
        <Box
          // Re-key per content so the outgoing layer remounts and replays the
          // fade-out from full opacity. `forwards` holds it transparent until it's
          // dropped, so it never flashes back.
          key={previous.key}
          position="absolute"
          top={0}
          left={0}
          w="100%"
          animation={`${fadeOut} ${CROSSFADE_MS}ms ease-in-out forwards`}
        >
          {previous.node}
        </Box>
      )}
      <Box
        // Re-key per content so the incoming layer remounts and replays the fade-in.
        // Old fades out and new fades in together — a true crossfade, so two opaque
        // layers never stack. Skip the animation on the very first paint.
        key={current.key}
        position="absolute"
        top={0}
        left={0}
        w="100%"
        animation={previous ? `${fadeIn} ${CROSSFADE_MS}ms ease-in-out` : undefined}
      >
        {current.node}
      </Box>
    </Box>
  );
};
